import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Button, Spin, Alert, message } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, SyncOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import BuildAppWizardStepper from './BuildAppWizardStepper';
import BuildAppResourcePicker from './BuildAppResourcePicker';
import { buildAppFinalize } from './buildAppApi';
import EFormDesigner from '../../EFormDesigner';
import MetaFlowBuilder from '../../MetaFlowBuilder';
import { WhatsAppWorkflowDesigner } from '../../../components/WorkflowDesigner';
import WhatsAppTemplateList from '../../WhatsAppTemplateList';
import DataSetManagementPage from '../../DataSetManagementPage';
import AiEmployeeProfilesPage from '../../AiEmployeeProfilesPage';
import KnowledgeBasePage from '../../KnowledgeBasePage';

const lbl = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

function authHeaders() {
  const token = localStorage.getItem('token') || '';
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function fetchEformRecord(id) {
  const res = await fetch(`/api/eforms/${id}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    htmlCode: data.htmlCode,
    formType: data.formType,
    fieldDisplaySettings: data.fieldDisplaySettings,
    metaFlowJson: data.metaFlowJson,
  };
}

/** Parse selectionsJson safely. Returns default selections if invalid. */
function parseSelectionsJson(json) {
  if (!json) return null;
  try {
    const parsed = typeof json === 'string' ? JSON.parse(json) : json;
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

const BuildAppWizardPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const wizardState = location.state || {};

  const steps = wizardState.steps || [];

  // ── 從 wizardState 還原已保存的選項（歷史載入時）
  const initialSelections = useMemo(() => {
    const saved = parseSelectionsJson(wizardState.selectionsJson);
    return {
      templateId: saved?.templateId ?? null,
      datasetId: saved?.datasetId ?? null,
      aiStaffId: saved?.aiStaffId ?? null,
      kbDocumentIds: saved?.kbDocumentIds ?? [],
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional: only parse once on mount from router state

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedIndices, setCompletedIndices] = useState([]);
  const [eformRecord, setEformRecord] = useState(null);
  const [metaflowRecord, setMetaflowRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  // 記住用戶在各步驟選用的資源（單選存 id 字串，KB 為 id 陣列）
  const [selections, setSelections] = useState(initialSelections);

  // 追蹤 workflow step 是否已完成 pre-finalize
  const [workflowFinalizeState, setWorkflowFinalizeState] = useState('idle'); // idle | running | done | error
  // 每次 pre-finalize 完成後改變 key，強制 WhatsAppWorkflowDesigner 重新載入
  const [workflowKey, setWorkflowKey] = useState(0);
  // 記錄 pre-finalize 時的選項快照，避免重複 finalize（選項未改變時）
  const lastFinalizedSelectionsRef = useRef(null);

  const updateSelection = useCallback((key, val) => {
    setSelections((prev) => ({ ...prev, [key]: val ?? (key === 'kbDocumentIds' ? [] : null) }));
    // 選項改變後，允許下次進入 workflow step 時重新 finalize
    setWorkflowFinalizeState('idle');
  }, []);

  const currentStepKey = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  useEffect(() => {
    if (!steps.length) {
      navigate('/studio/build-app', { replace: true });
    }
  }, [steps.length, navigate]);

  // ── 載入 eform / metaflow record ──
  useEffect(() => {
    const load = async () => {
      if (currentStepKey === 'eform' && wizardState.eformId && !eformRecord) {
        setLoadingRecord(true);
        try {
          setEformRecord(await fetchEformRecord(wizardState.eformId));
        } catch {
          message.error(lbl(t, 'buildApp.wizardLoadFail', '載入表單失敗'));
        } finally {
          setLoadingRecord(false);
        }
      }
      if (currentStepKey === 'metaflow' && wizardState.metaflowId && !metaflowRecord) {
        setLoadingRecord(true);
        try {
          setMetaflowRecord(await fetchEformRecord(wizardState.metaflowId));
        } catch {
          message.error(lbl(t, 'buildApp.wizardLoadFail', '載入 MetaFlow 失敗'));
        } finally {
          setLoadingRecord(false);
        }
      }
    };
    load();
  }, [currentStepKey, wizardState.eformId, wizardState.metaflowId, eformRecord, metaflowRecord, t]);

  // ── Pre-finalize：進入 workflow step 時先把選用資源寫入 workflow 節點 ──
  useEffect(() => {
    if (currentStepKey !== 'workflow') return;
    if (!wizardState.workflowId) return;
    if (workflowFinalizeState !== 'idle') return;

    // 比對選項是否有改變（避免無謂重複呼叫）
    const selSnapshot = JSON.stringify(selections);
    if (lastFinalizedSelectionsRef.current === selSnapshot && workflowFinalizeState === 'done') return;

    const doPreFinalize = async () => {
      setWorkflowFinalizeState('running');
      try {
        await buildAppFinalize({
          workflowId: wizardState.workflowId,
          eformId: wizardState.eformId || null,
          metaflowId: wizardState.metaflowId || null,
          templateId: selections.templateId || null,
          datasetId: selections.datasetId || null,
          aiStaffId: selections.aiStaffId || null,
          kbDocumentIds: selections.kbDocumentIds || [],
          buildAppId: wizardState.buildAppId || null,
          selectionsJson: JSON.stringify(selections),
        });
        lastFinalizedSelectionsRef.current = selSnapshot;
        // 強制 WorkflowDesigner 重新載入（讀取已更新的 workflow JSON）
        setWorkflowKey((k) => k + 1);
        setWorkflowFinalizeState('done');
        message.success(lbl(t, 'buildApp.wizardPreFinalizeDone', '已將選用設定套用到流程節點'));
      } catch {
        setWorkflowFinalizeState('error');
        message.warning(lbl(t, 'buildApp.wizardFinalizeFail', '流程資源關聯失敗，可稍後於流程設計器手動設定'));
      }
    };
    doPreFinalize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepKey, workflowFinalizeState]);

  const handlePrev = useCallback(() => {
    if (currentStepIndex > 0) setCurrentStepIndex((i) => i - 1);
  }, [currentStepIndex]);

  const handleNext = useCallback(async () => {
    setCompletedIndices((prev) =>
      prev.includes(currentStepIndex) ? prev : [...prev, currentStepIndex]
    );

    if (isLastStep) {
      // 如果最後一步是 workflow，pre-finalize 已在進入時執行；
      // 如果沒有 workflow step（罕見），在此補一次 finalize
      if (currentStepKey !== 'workflow' && wizardState.workflowId) {
        setFinalizing(true);
        try {
          await buildAppFinalize({
            workflowId: wizardState.workflowId,
            eformId: wizardState.eformId || null,
            metaflowId: wizardState.metaflowId || null,
            templateId: selections.templateId || null,
            datasetId: selections.datasetId || null,
            aiStaffId: selections.aiStaffId || null,
            kbDocumentIds: selections.kbDocumentIds || [],
            buildAppId: wizardState.buildAppId || null,
            selectionsJson: JSON.stringify(selections),
          });
        } catch {
          message.warning(lbl(t, 'buildApp.wizardFinalizeFail', '流程資源關聯失敗，可稍後於流程設計器手動設定'));
        } finally {
          setFinalizing(false);
        }
      }
      message.success(lbl(t, 'buildApp.wizardDone', '建置完成！'));
      navigate('/studio/build-app', { replace: true });
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  }, [currentStepIndex, isLastStep, currentStepKey, navigate, t, wizardState, selections]);

  const stepHint = useMemo(() => {
    const hints = {
      eform: lbl(t, 'buildApp.wizardHint.eform', '在此調整表單欄位與版面，確認後進入下一步。'),
      metaflow: lbl(t, 'buildApp.wizardHint.metaflow', '在此調整 Meta Flow 畫面，確認後進入下一步。'),
      template: lbl(t, 'buildApp.wizardHint.template', '請先在下方建立或確認模板，再於上方選用欄選取要套用到流程的模板。'),
      dataset: lbl(t, 'buildApp.wizardHint.dataset', '請先在下方確認 Dataset 設定，再於上方選用欄選取要套用到流程的 Dataset。'),
      aiStaff: lbl(t, 'buildApp.wizardHint.aiStaff', '請先在下方建立或確認 AI 員工檔案，再於上方選用欄選取要套用到流程的 AI 員工。'),
      kb: lbl(t, 'buildApp.wizardHint.kb', '請先在下方上傳知識庫文件，再於上方選用欄選取要套用的文件（可多選）。'),
      workflow: lbl(t, 'buildApp.wizardHint.workflow', '已將選用的表單、模板、AI 員工、知識庫等設定自動套用到流程節點，可在此繼續完善設置。'),
    };
    return hints[currentStepKey] || '';
  }, [currentStepKey, t]);

  // ── 渲染各步驟的選用欄（template / dataset / aiStaff / kb 才顯示）──
  const renderPicker = () => {
    if (!['template', 'dataset', 'aiStaff', 'kb'].includes(currentStepKey)) return null;

    const pickerStepKey = currentStepKey;
    const pickerValue =
      pickerStepKey === 'template' ? selections.templateId
      : pickerStepKey === 'dataset' ? selections.datasetId
      : pickerStepKey === 'aiStaff' ? selections.aiStaffId
      : selections.kbDocumentIds;

    const pickerDefaultId =
      pickerStepKey === 'template' ? (selections.templateId || wizardState.templateId)
      : pickerStepKey === 'dataset' ? (selections.datasetId || wizardState.datasetId)
      : pickerStepKey === 'aiStaff' ? selections.aiStaffId   // already in selections from restore
      : undefined;

    const pickerKey =
      pickerStepKey === 'template' ? 'templateId'
      : pickerStepKey === 'dataset' ? 'datasetId'
      : pickerStepKey === 'aiStaff' ? 'aiStaffId'
      : 'kbDocumentIds';

    return (
      <BuildAppResourcePicker
        stepKey={pickerStepKey}
        value={pickerValue}
        onChange={(val) => updateSelection(pickerKey, val)}
        defaultId={pickerDefaultId}
        labels={{
          pick: lbl(t, 'buildApp.wizardPickLabel', '選用於流程：'),
          placeholder: lbl(t, 'buildApp.wizardPickPlaceholder', '選擇要用於流程的項目（可先在下方新增再重新整理）'),
          selected: lbl(t, 'buildApp.wizardPickSelected', '已選用'),
          refresh: lbl(t, 'buildApp.wizardPickRefresh', '重新整理'),
        }}
      />
    );
  };

  // ── 渲染各步驟內容 ──
  const renderStepContent = () => {
    if (loadingRecord) {
      return (
        <div style={styles.centered}>
          <Spin size="large" tip={lbl(t, 'buildApp.wizardLoading', '載入中…')} />
        </div>
      );
    }

    switch (currentStepKey) {
      case 'eform':
        return eformRecord ? (
          <EFormDesigner
            initialSchema={eformRecord}
            onSave={() => message.success(lbl(t, 'buildApp.wizardSaved', '已儲存'))}
            onBack={handlePrev}
          />
        ) : (
          <div style={styles.centered}>
            <Spin tip={lbl(t, 'buildApp.wizardLoading', '載入中…')} />
          </div>
        );

      case 'metaflow':
        return metaflowRecord ? (
          <MetaFlowBuilder
            initialSchema={metaflowRecord}
            onSave={() => message.success(lbl(t, 'buildApp.wizardSaved', '已儲存'))}
            onBack={handlePrev}
          />
        ) : (
          <div style={styles.centered}>
            <Spin tip={lbl(t, 'buildApp.wizardLoading', '載入中…')} />
          </div>
        );

      case 'template':
        return (
          <div style={styles.embeddedPage}>
            <WhatsAppTemplateList />
          </div>
        );

      case 'dataset':
        return (
          <div style={styles.embeddedPage}>
            <DataSetManagementPage />
          </div>
        );

      case 'aiStaff':
        return (
          <div style={styles.embeddedPage}>
            <AiEmployeeProfilesPage />
          </div>
        );

      case 'kb':
        return (
          <div style={styles.embeddedPage}>
            <KnowledgeBasePage />
          </div>
        );

      case 'workflow':
        // 正在 pre-finalize 中：顯示 loading overlay
        if (workflowFinalizeState === 'running') {
          return (
            <div style={styles.centered}>
              <Spin
                size="large"
                indicator={<SyncOutlined spin style={{ fontSize: 32, color: '#7234CF' }} />}
                tip={lbl(t, 'buildApp.wizardPreFinalizing', '正在套用選用設定到流程節點，請稍候…')}
              />
            </div>
          );
        }
        return (
          <div style={styles.workflowEmbed}>
            <WhatsAppWorkflowDesigner
              key={workflowKey}
              workflowId={wizardState.workflowId}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (!steps.length) return null;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/studio/build-app')}
        >
          {lbl(t, 'buildApp.wizardBackToChat', '返回 Build App')}
        </Button>
        <span style={styles.headerTitle}>
          {lbl(t, 'buildApp.wizardTitle', '建置嚮導')}
        </span>
        <span style={styles.stepCounter}>
          {currentStepIndex + 1} / {steps.length}
        </span>
      </div>

      <BuildAppWizardStepper
        steps={steps}
        currentIndex={currentStepIndex}
        completedIndices={completedIndices}
        t={t}
      />

      {stepHint && (
        <div style={styles.hintBar}>{stepHint}</div>
      )}

      {/* finalize 出錯時提示（workflow step 頂部） */}
      {currentStepKey === 'workflow' && workflowFinalizeState === 'error' && (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ flexShrink: 0, margin: '0 16px 4px' }}
          message={lbl(t, 'buildApp.wizardFinalizeFail', '部分資源自動關聯失敗，可在流程設計器中手動設定節點屬性')}
          action={
            <Button
              size="small"
              onClick={() => setWorkflowFinalizeState('idle')}
            >
              重試
            </Button>
          }
        />
      )}

      {renderPicker()}

      <div style={styles.content}>
        {renderStepContent()}
      </div>

      <div style={styles.footer}>
        <Button
          disabled={currentStepIndex === 0}
          onClick={handlePrev}
          icon={<ArrowLeftOutlined />}
        >
          {lbl(t, 'buildApp.wizardPrev', '上一步')}
        </Button>
        <Button
          type="primary"
          onClick={handleNext}
          loading={finalizing || workflowFinalizeState === 'running'}
          disabled={workflowFinalizeState === 'running'}
          icon={isLastStep ? <CheckOutlined /> : <ArrowRightOutlined />}
          style={{ background: '#7234CF', borderColor: '#7234CF' }}
        >
          {isLastStep
            ? lbl(t, 'buildApp.wizardFinish', '完成建置')
            : lbl(t, 'buildApp.wizardNext', '確認，下一步')}
        </Button>
      </div>
    </div>
  );
};

const styles = {
  page: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: '#f7f7f7',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 16px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 600,
    fontSize: 16,
    color: '#333',
    flex: 1,
  },
  stepCounter: {
    color: '#888',
    fontSize: 13,
  },
  hintBar: {
    padding: '8px 24px',
    background: '#faf5ff',
    borderBottom: '1px solid #ede9fe',
    color: '#7234CF',
    fontSize: 13,
    flexShrink: 0,
  },
  content: {
    flex: 1,
    overflow: 'hidden',
    position: 'relative',
    minHeight: 0,
  },
  embeddedPage: {
    height: '100%',
    overflow: 'auto',
  },
  workflowEmbed: {
    height: '100%',
    overflow: 'hidden',
  },
  centered: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 16,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    background: '#fff',
    borderTop: '1px solid #f0f0f0',
    flexShrink: 0,
    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
  },
};

export default BuildAppWizardPage;
